/** Small DOM helpers shared by the HUD overlays. */

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

export interface Modal {
  overlay: HTMLDivElement;
  body: HTMLDivElement;
  actions: HTMLDivElement;
  close(): void;
  readonly open: boolean;
}

/** Create a modal inside `root`. Returns handles; call close() to remove it. */
export function createModal(root: HTMLElement, o: { title: string; size?: 'wide' | 'narrow'; closeLabel?: string; onClose?: () => void; dismissible?: boolean }): Modal {
  const overlay = el('div', 'slot-overlay');
  const modal = el('div', `slot-modal${o.size ? ` slot-modal--${o.size}` : ''}`);
  const head = el('div', 'slot-modal__head');
  const title = el('h2', 'slot-modal__title', o.title);
  const body = el('div', 'slot-modal__body');
  const actions = el('div', 'slot-modal__actions');
  actions.hidden = true;
  head.appendChild(title);
  let isOpen = true;
  const close = () => {
    if (!isOpen) return;
    isOpen = false;
    overlay.remove();
    o.onClose?.();
  };
  if (o.dismissible !== false) {
    const closeBtn = el('button', 'slot-modal__close', '×');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', o.closeLabel ?? 'Close');
    closeBtn.addEventListener('click', close);
    head.appendChild(closeBtn);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
  }
  modal.append(head, body, actions);
  overlay.appendChild(modal);
  root.appendChild(overlay);
  return {
    overlay,
    body,
    actions,
    close,
    get open() {
      return isOpen;
    },
  };
}
